<template id="bs-modal-checkin">
<div class="modal fade" id="myModal" tabindex="-1" role="dialog" ref="checkinModal">
<div class="modal-dialog modal-lg" role="document">
<div class="modal-content">

	<div class="text-gray-500 font-weight-bold text-uppercase text-base bg-gray-100 px-3 py-25 rounded-top border-bottom" data-test="modalTitle">
		{{ modalTitle }}
		<button type="button" class="close" data-dismiss="modal" aria-label="Close" data-test="checkinModalClose">
        	<span aria-hidden="true" @click="resetForm()">&times;</span>
        </button>
	</div>

	<div class="modal-body py-0 px-3">
		<form @submit.prevent="submitForm" @keydown="form.onKeydown($event)" @change="form.onKeydown($event)" class="mb-0">
			<div class="row">
				<div class="col-md-5 pt-3 pb-3">
					<div class="flex-grow-1">

						<alert-errors :form="form">There were some problems with your input.</alert-errors>
						
						<div v-if="showProviderSelection" class="form-group">
							<!-- show select option buttons -->
							<div class="d-flex align-items-baseline">
								<div class="flex-grow-1">
									<i  
										v-show="showNearbyProviders"
										class="fas fa-redo-alt text-gray-500 refresh" :class="{ 'fa-spin' : !nearby_populated }"
										@click="getNearbyProviders()" />
								</div>
								<div class="btn-group mb-2">
									<button
										type="button"
										class="btn btn-sm leading-tight" :class="showNearbyProviders ? 'btn-secondary' : 'btn-light'"
										:disabled="!locationEnabled"
										@click="displayNearbyProviders()"
										data-test="showNearbyProviders">
										<span v-tooltip.bottom="locationEnabled ? '' : 'Requires location'">Nearby</span>
									</button>
									<button
										type="button"
										class="btn btn-sm leading-tight" :class="showAllProviders ? 'btn-secondary' : 'btn-light'"
										@click="displayAllProviders()"
										data-test="showAllProviders">
										Show all
									</button>
								</div>
							</div>

							<!-- NEARBY PHYSICIANS -- ember and google results  -->
							<v-select
								v-if="showNearbyProviders"
								placeholder="Select Provider"
								label="display_name"
								:options="nearby_results"								
								:selectable="option => option.selectable == true || option.selectable == undefined"
								v-model="selected_nearby_physician"
								data-test="checkinPhysician"
							>								    
							    <template slot="option" slot-scope="option">
							    	<div
							    		v-if="option.selectable == false"
							    		class="text-sm text-uppercase font-weight-bold border-bottom text-gray-500 mt-2 pb-1 mb-1"
							    		v-html="option.display_name"
							    	/>
							        <div v-else style="overflow-x:hidden">
						        		<span class="leading-none font-weight-bold" v-html="option.display_name" /><br>
						        		<span class="text-sm" v-html="option.address ? option.address : option.short_address" />
							    	</div>
							    </template>
							</v-select>

							<!-- ALL PHYSICIANS -->
							<template v-else>
								<v-select
									placeholder="Select Provider"
									label="display_name"
									:options="checkin_options.ex_providers ? mappableProviderIdsExRoute : allProviderIds"	
									v-model="form.checkinables"
									:class="{ 'is-invalid' : form.errors.has('checkinables') }"
									data-test="checkinPhysician"
								>
									<template v-if="checkin_options.ex_providers" v-slot:option="option">
										<div class="leading-none font-weight-bold">{{ option.display_name }}</div>
										<div class="text-sm">{{ option.short_address }}</div>
									</template>										
								</v-select>
								<div v-if="!locationEnabled" class="text-sm mt-2 py-2 px-25 alert alert-warning leading-tight">
									Enable location services to search for nearby physicians
								</div>
							</template>
						</div>

						<v-select
							class="form-group"
							placeholder="Check-in Type"
							:searchable="false"
							label="label"
							:options="options.checkin_types"
							:reduce="label => label.id"
							v-model="form.checkin_type_id"
							data-test="checkinType" />										
						<has-error :form="form" field="checkin_type_id" />
					
						<div v-if="form.checkin_type_id == 8" class="alert alert-warning mb-3 px-2 py-1 leading-tight text-sm">Note that Luvo EMR sent <strong>e-faxes are auto-imported</strong> 24 hours after sending.</div>

						<v-select
							class="form-group"
							placeholder="Service Lines (optional)"
							:searchable="false"
							multiple
							label="label"
							:options="options.service_lines"
							:reduce="label => label.id"
							v-model="form.service_lines"
							:class="{ 'is-invalid' : form.errors.has('service_lines') }"
							data-test="checkinServiceLines" />
						<has-error :form="form" field="service_lines" />

						<div 
							@click="toggleMoreOptions()"
							class="mb-2 cursor-pointer"
							:class="{ 'text-blue-700 font-weight-bold' :showMoreOptions }"
							data-test="checkinOptions">
							More Options
							<i class="fas" :class="showMoreOptions ? 'fa-caret-down' : 'fa-caret-right text-gray-500'"></i>
						</div>

						<template v-if="showMoreOptions">
							<v-select
								v-if="!checkin_options.route"
								class="form-group"
								placeholder="Route (optional)"
								label="title"
								:options="options.routes"
								:reduce="title => title.id"
								v-model="form.route_id"
								data-test="checkinRoute" />								

							<v-select
								v-if="!checkin_options.campaign_id"
								class="form-group"
								placeholder="Campaign (optional)"
								label="title"
								:options="options.campaigns"
								:reduce="title => title.id"
								v-model="form.campaign_id"
								data-test="checkinCampaign" />											

							<v-select
								class="form-group"
								placeholder="Assets used (optional)"
								multiple
								label="name"
								:options="options.assets"
								:reduce="name => name.id"
								v-model="form.assets"
								:class="{ 'is-invalid' : form.errors.has('assets') }"
								data-test="checkinAssets"
							>
								<template slot="option" slot-scope="option">
									<img :src="option.thumbnail_url" width=20 height=20>
									{{ option.name }}
								</template>
							</v-select>

						</template>						
					</div>

					<div class="d-none d-md-flex button-wrap pt-3 px-3 t justify-content-between">
						<span class="btn btn btn-ghost-secondary" @click="resetForm()" data-dismiss="modal" aria-label="Close" data-test="checkinModalClose">Cancel</span>
						<button :disabled="form.busy || !form.checkin_type_id" type="submit" class="btn btn-primary" data-test="saveCheckinButton">Add Check-in</button>
					</div>
				</div>

				<div class="checkin-notes col-md-7 pb-0 pt-md-4 pt-3 bg-gray-200">
					<div class="form-group">
			            <vue-editor
			            	placeholder="Check-in notes"
			                :editor-toolbar="config.editor.toolbar"
			                v-model="form.notes"
			                data-test="checkinNotes"
			            />
						<has-error :form="form" field="notes" />
					</div>
					<div class="d-md-none form-group d-flex justify-content-between">
						<span class="btn btn btn-ghost-secondary" @click="resetForm()" data-dismiss="modal" aria-label="Close" data-test="checkinModalClose">Cancel</span>						
						<button :disabled="form.busy || !form.checkin_type_id" type="submit" class="btn btn-primary" data-test="saveCheckinButton">Add Check-in</button>
					</div>
				</div>
			</div>
		</form>

	</div>
</div>
</div>
</div>
</template>

<script>

	var stringSimilarity = require('string-similarity');

	import { mapGetters, mapState } from 'vuex';
	import { Form, HasError, AlertError, AlertErrors } from 'vform';
	import { gmapApi } from 'vue2-google-maps';

  	export default {

        components: {
            HasError,
            AlertError,
            AlertErrors,
            Form            
        },

	    data () {
	        return {
	        	modalTitle: 'Create Check-in',

	        	checkin_options: {
	        		route: null,
	        		bulk_save: false,
	        		campaign_id: null,
	        		location_id: null,
	        		checkinables: [],
	        		ex_providers: null,
	        	},

	        	locationEnabled: null,

				form: new Form({
					checkinables: null,
					route_id: null,
					campaign_id: null,
					location_id: null,
					service_lines: [],
					checkin_type_id: null,
					assets: [],
					notes: null,
					latitude: null,
					longitude: null,
					bulk_save: false,					
				}),

	            selected_physician: '',
	            selected_nearby_physician: null,

				showNearbyProviders: false,
				showAllProviders: false,

	            showMoreOptions: false,
	            nearby_populated: false,
	            radius: 2500,
	            ember_nearby: [],
	            google_nearby: [],
	        }
	    },

		mounted() {
			//Maybe not call these all the time?
	    	this.$store.dispatch('fetchAssets');

			//populate all the things!
			this.$root.$on('build_checkin_modal', data => {
				this.checkin_options = data.checkin_options;

				if (this.checkin_options.checkinables) {
					this.checkin_options.bulk_save ? this.form.bulk_save = this.checkin_options.bulk_save : this.form.bulk_save = null;
					this.form.checkinables = this.checkin_options.checkinables;
				}

				this.form.route_id = this.checkin_options.route;
				this.form.campaign_id = this.checkin_options.campaign_id;
				this.form.location_id = this.checkin_options.location_id;

				this.getGeo();
				this.setProviderListType();
				this.setModalTitle();
			});  	
		},

	    methods: {
		    createCheckin() {
		     	this.form.post('/api/checkins/store').then(({ data }) => { 
		        	//return the new checkin(s)
		        	this.$root.$emit('checkinadded', data);
		        	this.resetForm();

		        	//forgive me, for I have sinned
		        	jQuery('#myModal').modal('hide');

                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 4000,
                        icon: 'success',
                        title: 'Check-in saved',
                    });	
		        })
		    },

		    createPhysicianFirst() {
		    	axios.post('/api/physicians/store-from-google', this.selected_nearby_physician).then(({ data }) => {
	    			Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 8000,
                        icon: 'success',
                        title: 'Physician record "' + data.display_name + '" added to system',
                    });	
	    			//set the new physician ID and clear the form
                    this.form.checkinables = {
                    	morphable_id: data.id,
                    	morphable_type: 'App\\Physician',
                    }
                    this.createCheckin();
                    
                    this.$store.dispatch('addNewPhysician', data);
                    this.$root.$emit('physician_added', true);
                    this.selected_nearby_physician = '';
	    		});
		    },

		  	displayAllProviders() {
		  		this.showAllProviders = true;
		  		this.showNearbyProviders = false;
		  	},

		  	displayNearbyProviders() {
		  		this.$gmapApiPromiseLazy().then(() => { 
					this.getNearbyProviders();
				});
		  		this.showNearbyProviders = true;
		  		this.showAllProviders = false;
		  	},

			getGeo() {
		        navigator.geolocation.getCurrentPosition(
		        	pos => { 
						this.form.latitude = pos.coords.latitude;
						this.form.longitude = pos.coords.longitude;
						this.locationEnabled = true;				
		        	},
		        	err => {
		        		this.locationEnabled = false;
		        		console.warn(`ERROR(${err.code}): ${err.message}`);
		        	},
		        	{
						enableHighAccuracy: true,
					},
		        );
			},

			getNearbyProviders() {
	            //Google search
		  		let service = new google.maps.places.PlacesService(document.createElement('div'));
		  		service.nearbySearch({
				    location: new google.maps.LatLng(this.form.latitude, this.form.longitude),
				    type: ['doctor'],
				    rankBy: google.maps.places.RankBy.DISTANCE
		  		},
		  		(results, status) => { this.google_nearby = results });

		  		this.nearby_populated = true;
			},

			resetForm() {
                this.selected_nearby_physician = '';
                this.form.checkinables = {};
                this.form.checkin_type_id = '';
                this.form.service_lines = '';
                this.form.notes = '';
                this.form.assets = '';
			},

			setModalTitle() {
    			this.checkin_options.bulk_save ? this.modalTitle = 'Bulk Create Check-ins with ' + this.checkin_options.checkinables.length + ' providers' : 
    				this.checkin_options.checkinables && this.checkin_options.checkinables.display_name ? 
    					 this.modalTitle = 'Create Check-in with ' + this.checkin_options.checkinables.display_name :  this.modalTitle = 'Create Check-in';
			},

		  	setProviderListType() {
		  		//on a route, with location services, show nearby by default. otherwise all.
		  		if (this.locationEnabled && this.checkin_options.route) {
		  			this.displayNearbyProviders();
		  		} else {
		  			this.displayAllProviders();
		  		}
		  	},

		    submitForm() {
		    	if (!this.checkin_options.checkinables && this.selected_nearby_physician && this.selected_nearby_physician.source === 'google') {
				    this.createPhysicianFirst();
			    } else {
				    this.createCheckin();
				}
		    },

		    toggleMoreOptions() {
		    	this.showMoreOptions = !this.showMoreOptions;
		    },
    	},

    	computed: {    		
    		...mapGetters(['allProviderIds', 'mappableProviderIds', 'nearbyProviderIds']),
    		...mapState(['config', 'options']),

    		mappableProviderIdsExRoute() {
				return this.mappableProviderIds.filter(referrer => !this.checkin_options.ex_providers.some(visit => visit.location_id == referrer.location_id));
    		},
    		
    		// modalTitle() {
    		// 	return this.checkin_options.bulk_save ? 
    		// 		'Bulk Create Check-ins with ' + this.checkin_options.checkinables.length + ' providers' : 
    		// 		this.checkin_options.checkinables && this.checkin_options.checkinables.display_name ? 
    		// 			'Create Check-in with ' + this.checkin_options.checkinables.display_name : 'Create Check-in';
    		// },

    		nearby_results() {
    			let combined_results = [];
    			let ember_nearby = this.nearbyProviderIds({ 
    				latitude: this.form.latitude,
    				longitude: this.form.longitude
    			});

    			if (ember_nearby && ember_nearby.length) {
    				combined_results.push({
						selectable: false,
						display_name: 'Existing Providers',
					});

    				//if on a route, remove locations already in the route
    				if (this.checkin_options.ex_providers) {
    					ember_nearby = ember_nearby.filter(referrer => !this.checkin_options.ex_providers.some(visit => visit.location_id == referrer.location_id));
    				}

    				combined_results = [...combined_results, ...ember_nearby];
    			}
				
				if (this.google_nearby.length) {
					//another header
					combined_results.push({
						selectable: false,
						display_name: 'Local Results (from Google)',
					});

					//TODO: BUG!? This is filtering physicians that are already on the route. Should it filter anything nearby?
					//if (this.checkin_options.ex_providers) {
						var ember_names = combined_results.map(ember => ember.display_name);
					//}
					//else {
					//	var ember_names = ['zoinks'];
					//}

					this.google_nearby.forEach(item => {
						//remove close matches that are already in ember
						if (stringSimilarity.findBestMatch(item.name, ember_names).bestMatch.rating < .65) {
							combined_results.push({
								source: 'google',
								display_name: item.name,
								address: item.vicinity,	
								location: { lat: item.geometry.location.lat(), lng: item.geometry.location.lng() }			
							});
						}
					});
				}

				return combined_results;
    		},

    		showProviderSelection() {
    			//!checkin_options.checkinables || !checkin_options.checkinables.morphable_id
    			if (this.checkin_options.bulk_save) {
    				return false;
    			}
    			if (this.checkin_options.checkinables && this.checkin_options.checkinables.morphable_id) {
    				return false;
    			}
    			return true;
    		}
    	},

    	watch: {
    		selected_nearby_physician() {
    			if (this.selected_nearby_physician.source === 'ember') {
	    			this.form.physician_id = this.selected_nearby_physician.id;
	    		}
    			if (this.selected_nearby_physician.source === 'google') {
	    			this.form.physician_id = null;
	    		}	    		
    		},
    	},

  };
</script>

<style scoped>

	.button-wrap {
		border-top:1px solid #e4e7ea;
		margin:0 -1rem; }

	.checkin-notes {
		box-shadow:inset 0px 7px 9px -7px rgba(0,0,0,0.4);
	}

	i.refresh { 
		cursor:pointer !important;
	}

	@media (min-width: 768px) { 
		.checkin-notes {
			box-shadow:inset 7px 0 9px -7px rgba(0,0,0,0.4);
		}
	}
</style>